import { createContext, useCallback, useContext, useState, useEffect } from 'react';
import axios from 'axios';

import { getCookie, setCookie, deleteCookie } from './utils/cookies';

// Note: We don't use withCredentials globally because:
// 1. We set cookies client-side, not server-side
// 2. We send auth token via Authorization header, not cookies
// 3. Backend uses Access-Control-Allow-Origin: * which doesn't work with credentials
// If backend needs to read cookies, it should set specific origins in CORS config

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper function to decode JWT token
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

// Helper function to check if token is expired
const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

export const AuthProvider = ({ children }) => {
  // Try to get token from cookie first, then localStorage (for migration)
  const getStoredToken = () => {
    const cookieToken = getCookie('access_token');
    if (cookieToken) return cookieToken;
    
    // Fallback to localStorage for migration
    const localToken = localStorage.getItem('access_token');
    if (localToken) {
      // Migrate to cookie
      setCookie('access_token', localToken);
      localStorage.removeItem('access_token');
      return localToken;
    }
    return null;
  };

  const [token, setToken] = useState(getStoredToken());
  const [user, setUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state function
  const initializeAuth = useCallback(() => {
    const storedToken = getStoredToken();
    
    if (storedToken && !isTokenExpired(storedToken)) {
      const decoded = decodeToken(storedToken);
      setToken(storedToken);
      setUser({
        email: decoded.email || '',
        is_admin: decoded.is_admin || false,
        preferred_username: decoded.email || '',
        user_id: decoded.id || '',
        username: decoded.username || '',
      });
      setIsAuthenticated(true);
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    } else {
      // Token expired or invalid
      if (storedToken) {
        deleteCookie('access_token');
        localStorage.removeItem('access_token'); // Clean up localStorage if exists
      }
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      delete axios.defaults.headers.common['Authorization'];
    }
    
    setIsInitialized(true);
  }, []);

  // Initial auth setup and interceptor
  useEffect(() => {
    initializeAuth();

    // Set up axios interceptor for 401 responses
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          // Token expired or invalid, logout
          deleteCookie('access_token');
          localStorage.removeItem('access_token'); // Clean up localStorage if exists
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
          delete axios.defaults.headers.common['Authorization'];
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [initializeAuth]);

  // Listen for cookie changes (for cross-instance session sharing)
  useEffect(() => {
    if (!isInitialized) return;

    // Check cookie every 2 seconds to detect changes from other instances
    const cookieCheckInterval = setInterval(() => {
      const currentCookieToken = getCookie('access_token');
      
      // If cookie changed, re-initialize auth
      if (currentCookieToken !== token) {
        initializeAuth();
      }
    }, 2000);

    return () => {
      clearInterval(cookieCheckInterval);
    };
  }, [isInitialized, token, initializeAuth]);

  const login = async (email, password, persist = false) => {
    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      // Build URL with persist parameter if needed
      let loginUrl = `${process.env.REACT_APP_API_URL}/token`;
      if (persist) {
        loginUrl += '?persist=true';
      }

      const response = await axios.post(
        loginUrl,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          withCredentials: false, // Explicitly disable credentials to work with Access-Control-Allow-Origin: *
        }
      );

      const { access_token } = response.data;
      
      if (access_token) {
        // Store token in cookie (shared across ports/domains)
        // If persist=true, set cookie for 365 days (1 year), otherwise 30 days
        const cookieDays = persist ? 365 : 30;
        setCookie('access_token', access_token, cookieDays);
        
        // Also set in localStorage as fallback (will be migrated to cookie on next load)
        localStorage.setItem('access_token', access_token);
        
        setToken(access_token);
        
        const decoded = decodeToken(access_token);
        setUser({
          email: decoded.email || email,
          is_admin: decoded.is_admin || false,
          preferred_username: decoded.email || email,
          user_id: decoded.id || '',
          username: decoded.username || '',
        });
        setIsAuthenticated(true);
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        return { success: true };
      }
      
      return { success: false, error: 'No token received' };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Error al iniciar sesión'
      };
    }
  };

  const logout = async () => {
    try {
      // Get token from cookie or localStorage
      const currentToken = getCookie('access_token') || localStorage.getItem('access_token');
      
      if (currentToken) {
        // Call logout endpoint
        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/logout`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${currentToken}`,
              },
              withCredentials: false, // Explicitly disable credentials to work with Access-Control-Allow-Origin: *
            }
          );
        } catch (error) {
          // Even if logout fails, clear local state
          console.error('Logout API error:', error);
        }
      }
      
      // Clear cookies and localStorage
      deleteCookie('access_token');
      localStorage.removeItem('access_token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      delete axios.defaults.headers.common['Authorization'];
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if API call fails
      deleteCookie('access_token');
      localStorage.removeItem('access_token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      delete axios.defaults.headers.common['Authorization'];
      return { success: true };
    }
  };

  const authValue = {
    // Token management
    token,
    user,
    isAuthenticated,
    isInitialized,
    
    // Auth methods
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};
